/**
 * Ein Ort fuer "welche Copy gehoert zu welchem OAuth-Fehlercode" (Findings R2-3, R3-3).
 *
 * Vorgeschichte: R2-3 hat GOOGLE_SCOPE_MISSING eingefuehrt und die Zuordnung
 * `code === 'GOOGLE_SCOPE_MISSING' ? … : …` an drei Stellen einzeln ausgeschrieben
 * (GoogleOAuthButton, ReauthDialog, indirekt GoogleCallback). Mit R3-3 kommen drei
 * weitere Codes dazu. Vier Codes mal drei handgeschriebene Ternaries sind zwoelf
 * Stellen, an denen genau eine vergessen werden kann, und der Diff eines vergessenen
 * Aufrufers sieht harmlos aus, weil er unveraendert bleibt. Deshalb hier zentral.
 *
 * Bewusst NICHT hier: der Text selbst. Der liegt in src/locales/{de,en}/common.json,
 * damit der Gedankenstrich-Guard (src/locales/noTypographicDashes.test.ts) ihn sieht.
 *
 * Bewusst NICHT hier: ein Default fuer unbekannte Codes. Jeder Aufrufer hat einen
 * eigenen, passenden Fallback ("Verbindung fehlgeschlagen" im Verbinden-Flow,
 * "Neuverbindung fehlgeschlagen" im Reauth-Dialog, "Anmeldung fehlgeschlagen" beim
 * Login). Ein gemeinsamer Default waere in zwei von drei Faellen falsch, also liefert
 * die Funktion `null` und der Aufrufer behaelt seinen eigenen Text.
 */

/** Consent lief durch, aber ohne den Merchant-API-Scope (R2-3). */
export const GOOGLE_SCOPE_MISSING = 'GOOGLE_SCOPE_MISSING';
/** Google war beim Profilabruf nicht erreichbar. Wiederholen hilft (R3-3). */
export const GOOGLE_PROFILE_UNAVAILABLE = 'GOOGLE_PROFILE_UNAVAILABLE';
/** Google hat die Identitaet nicht herausgegeben. Wiederholen hilft NICHT (R3-3). */
export const GOOGLE_PROFILE_FORBIDDEN = 'GOOGLE_PROFILE_FORBIDDEN';
/** Google hat etwas Unlesbares geantwortet. Weder Wiederholen noch Consent hilft (R3-3). */
export const GOOGLE_PROFILE_UNEXPECTED = 'GOOGLE_PROFILE_UNEXPECTED';

interface CopyKeys {
  readonly title: string;
  readonly description: string;
}

/** Code -> i18n-Schluessel im Namespace `common`. */
export const OAUTH_ERROR_COPY_KEYS: Readonly<Record<string, CopyKeys>> = {
  [GOOGLE_SCOPE_MISSING]: {
    title: 'oauth.scopeMissing',
    description: 'oauth.scopeMissingDesc',
  },
  [GOOGLE_PROFILE_UNAVAILABLE]: {
    title: 'oauth.profileUnavailable',
    description: 'oauth.profileUnavailableDesc',
  },
  [GOOGLE_PROFILE_FORBIDDEN]: {
    title: 'oauth.profileForbidden',
    description: 'oauth.profileForbiddenDesc',
  },
  [GOOGLE_PROFILE_UNEXPECTED]: {
    title: 'oauth.profileUnexpected',
    description: 'oauth.profileUnexpectedDesc',
  },
};

/** Codes, bei denen die Antwort des Handlers KEIN Konto und KEINE Session erzeugt hat. */
const PROFILE_FAILURE_CODES = new Set<string>([
  GOOGLE_PROFILE_UNAVAILABLE,
  GOOGLE_PROFILE_FORBIDDEN,
  GOOGLE_PROFILE_UNEXPECTED,
]);

export interface OAuthErrorCopy {
  readonly code: string;
  readonly title: string;
  readonly description: string;
}

/**
 * Loest einen Fehlercode in fertige Copy auf.
 *
 * @param code  beliebiger Wert aus einer Netzwerkantwort oder postMessage. Absichtlich
 *              `unknown`: die Quelle ist nicht typisiert, und ein `as string` waere hier
 *              genau die Luege, die spaeter `undefined` durch die Oberflaeche traegt.
 * @param t     i18next-`t` fuer den Namespace `common`.
 * @returns     `null` fuer unbekannte Codes, damit der Aufrufer seinen eigenen,
 *              passenderen Fallback behaelt.
 */
export function resolveOAuthErrorCopy(
  code: unknown,
  t: (key: string) => string,
): OAuthErrorCopy | null {
  if (typeof code !== 'string') return null;
  // hasOwnProperty statt eines schlichten Truthy-Checks: `code = 'toString'` oder
  // `'__proto__'` liefert sonst ein geerbtes Object.prototype-Mitglied, das truthy ist.
  // Der Aufruf ginge weiter mit `t(undefined)` und der Nutzer saehe eine leere Meldung.
  if (!Object.prototype.hasOwnProperty.call(OAUTH_ERROR_COPY_KEYS, code)) return null;
  const keys = OAUTH_ERROR_COPY_KEYS[code];
  return { code, title: t(keys.title), description: t(keys.description) };
}

/**
 * True, wenn der Code fuer einen fehlgeschlagenen Profilabruf steht (R3-3).
 *
 * Wichtig fuer GoogleCallback: bei GOOGLE_SCOPE_MISSING existiert eine
 * google_accounts-Zeile, und in der App wartet ein Banner mit Reparaturpfad. Bei diesen
 * drei Codes existiert nichts, worauf man den Nutzer weiterschicken koennte.
 */
export function isProfileFailureCode(code: unknown): boolean {
  return typeof code === 'string' && PROFILE_FAILURE_CODES.has(code);
}

/**
 * Liest den JSON-Body aus einem Edge-Function-Fehler von `supabase.functions.invoke`.
 *
 * Warum das noetig ist: der Client wirft bei jedem Non-2xx einen `FunctionsHttpError`
 * mit der pauschalen Meldung "Edge Function returned a non-2xx status code" und legt die
 * rohe `Response` unter `.context` ab, ohne sie zu lesen. Ohne diesen Helfer sieht der
 * Aufrufer nur diesen Satz, und der strukturierte `code` aus R3-3 waere unerreichbar.
 *
 * Wirft nie: schlaegt das Auslesen fehl, faellt der Aufrufer auf sein bisheriges
 * Verhalten zurueck. Ein Parserfehler darf keinen zweiten Fehler erzeugen.
 */
export async function readEdgeFunctionErrorBody(
  error: unknown,
): Promise<Record<string, unknown> | null> {
  const context = (error as { context?: unknown } | null | undefined)?.context as
    | Response
    | undefined;

  if (!context || typeof context.json !== 'function') return null;
  // Der Client liest den Body bei Non-2xx nicht, aber ein zweiter Aufruf (z.B. nach
  // einem Retry weiter oben) haette ihn schon verbraucht. Dann lieber null.
  if (context.bodyUsed) return null;

  try {
    const body = await context.json();
    return body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}
